/**
 * Eigene Krankheitstage einer Person — ein Baustein für Mitarbeiter- und
 * Onboarding-Fenster (Spezifikation 2026-09-16, R5).
 *
 * Arbeitstage ohne „Kind krank". Immer sichtbar, auch bei 0 (Maxi im
 * Personal-Cockpit-Daily 17.09.2026: erst einmal anzeigen). Rot heißt: das
 * Bindungs-Signal löst aus; orange: Beobachtung.
 * Vorher hatten beide Fenster je eine eigene Kopie mit verschiedenen Farben.
 */
import { Thermometer } from "lucide-react";
import {
  SCHWELLEN_EIGENE_KRANKHEITSTAGE as GRENZE,
  type Stufe,
  krankheitsStufe,
} from "../lib/krankheitstage.js";
import { KRANKHEIT_ZUSATZ_SEIT_EINTRITT, krankheitKachel } from "../lib/probezeit-anzeige.js";
import { KpiInfo } from "./ui/KpiInfo.js";

interface Werte {
  sickDays12m: number;
  sickDays3m: number;
  sickEpisodes12m: number;
  lastSickDate: string | null;
}

const FARBEN: Record<"rot" | "orange", { farbe: string; hintergrund: string | null }> = {
  rot: { farbe: "var(--risk-high)", hintergrund: "var(--risk-high-bg)" },
  orange: { farbe: "var(--risk-medium)", hintergrund: null },
};

function Kachel({
  wert,
  beschriftung,
  stufe,
}: { wert: number; beschriftung: string; stufe: Stufe }) {
  const { farbe, hintergrund } = stufe
    ? FARBEN[stufe]
    : { farbe: "var(--text-primary)", hintergrund: null };
  return (
    <div
      className="rounded-lg p-3 text-center"
      style={{
        background: hintergrund ?? "var(--bg-card)",
        border: `1px solid ${hintergrund ?? "var(--border)"}`,
      }}
    >
      <div className="text-lg font-bold font-mono-data" style={{ color: farbe }}>
        {wert.toLocaleString("de-DE")}
      </div>
      <div
        className="text-[9px] uppercase tracking-wider mt-0.5"
        style={{ color: "var(--text-muted)" }}
      >
        {beschriftung}
      </div>
    </div>
  );
}

export function EigeneKrankheitstage({
  werte,
  seitEintritt = false,
}: {
  werte: Werte;
  /**
   * In der Probezeit heißen die beiden 12-Monats-Kacheln „seit Eintritt": Es ist
   * derselbe Zeitraum, und die Liste „In der Probezeit" zeigt genau diese Werte.
   * Ohne Angabe bleibt alles wie bisher. Die Texte stehen in `probezeit-anzeige.ts`.
   */
  seitEintritt?: boolean;
}) {
  const beschriftung = krankheitKachel(seitEintritt);
  return (
    <div>
      <p
        className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-3"
        style={{ color: "var(--text-muted)" }}
      >
        <Thermometer size={11} />
        Eigene Krankheitstage
        <KpiInfo
          schluessel="eigeneKrankheitstage"
          label="Eigene Krankheitstage"
          zusatz={seitEintritt ? KRANKHEIT_ZUSATZ_SEIT_EINTRITT : undefined}
        />
      </p>
      <div className="grid grid-cols-3 gap-2">
        <Kachel
          wert={werte.sickDays12m}
          beschriftung={beschriftung.tage}
          stufe={krankheitsStufe(werte.sickDays12m, GRENZE.tage12m)}
        />
        <Kachel
          wert={werte.sickDays3m}
          beschriftung="Arbeitstage / 3M"
          stufe={krankheitsStufe(werte.sickDays3m, GRENZE.tage3m)}
        />
        <Kachel
          wert={werte.sickEpisodes12m}
          beschriftung={beschriftung.meldungen}
          stufe={krankheitsStufe(werte.sickEpisodes12m, GRENZE.meldungen12m)}
        />
      </div>
      {werte.lastSickDate && (
        <p className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
          Letzte Krankmeldung: {new Date(werte.lastSickDate).toLocaleDateString("de-DE")}
        </p>
      )}
    </div>
  );
}
